import { useEffect, useMemo, useState } from "react"
import { faCalendarCheck, faClose, faMagnifyingGlass, faPeopleGroup, faUser } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { BasicCard } from "../../../components/BasicCard/BasicCard"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import type { CustomerEntity } from "../../../Objects/Customer/CustomerEntity"
import { customerMemory } from "../../../Objects/Memory/Memory"
import type {
  RestaurantReservation,
  RestaurantReservationArea,
  RestaurantReservationSettings,
  RestaurantReservationSlot,
  SaveManagementRestaurantReservation,
} from "../../../Objects/RestaurantReservation/RestaurantReservationTypes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"
import { CostumerList } from "../Order/Components/CustomerList"

interface ReservationSavePopupProps {
  reservation?: RestaurantReservation
  settings: RestaurantReservationSettings
  areas: RestaurantReservationArea[]
  customers: CustomerEntity[]
  isLoading: boolean
  onClose: () => void
  onLoadAvailability: (
    areaId: number,
    date: string,
    partySize: number,
    reservationId?: number,
  ) => Promise<RestaurantReservationSlot[] | undefined>
  onSave: (request: SaveManagementRestaurantReservation) => Promise<boolean>
}

const toDateInput = (value: Date) => {
  const offsetDate = new Date(value.getTime() - value.getTimezoneOffset() * 60_000)
  return offsetDate.toISOString().slice(0, 10)
}

export const ReservationSavePopup: React.FC<ReservationSavePopupProps> = ({
  reservation,
  settings,
  areas,
  customers,
  isLoading,
  onClose,
  onLoadAvailability,
  onSave,
}) => {
  const { t } = useTranslation("restaurantReservations")

  const activeAreas = useMemo(() => areas.filter((area) => area.isActive), [areas])

  const availableCustomers = useMemo(() => customers.filter((customer) => !customer.isDefaultCustomer), [customers])

  const initialStart = reservation ? new Date(reservation.startsAt) : null

  const [areaId, setAreaId] = useState(reservation?.areaId ?? 0)

  const [customerId, setCustomerId] = useState(reservation?.customerId ?? 0)

  const [isCustomerSelectionOpen, setIsCustomerSelectionOpen] = useState(false)

  const [date, setDate] = useState(initialStart ? toDateInput(initialStart) : toDateInput(new Date()))

  const [partySize, setPartySize] = useState(String(reservation?.partySize ?? Math.max(settings.minimumPartySize, 2)))

  const [time, setTime] = useState(
    initialStart?.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) ?? "",
  )

  const [notes, setNotes] = useState(reservation?.notes ?? "")

  const [slots, setSlots] = useState<RestaurantReservationSlot[]>([])

  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false)

  const minimumDate = toDateInput(new Date())

  const maximumDate = useMemo(() => {
    const maximum = new Date()
    maximum.setDate(maximum.getDate() + settings.maximumAdvanceDays)
    return toDateInput(maximum)
  }, [settings.maximumAdvanceDays])

  const selectedAreaId = activeAreas.some((area) => area.id === areaId) ? areaId : (activeAreas[0]?.id ?? 0)

  const selectedCustomer =
    availableCustomers.find((customer) => customer.id === customerId) ?? customerMemory.findById(customerId)

  const selectedCustomerId = selectedCustomer && !selectedCustomer.isDefaultCustomer ? selectedCustomer.id : 0

  const selectedArea = activeAreas.find((area) => area.id === selectedAreaId)

  const maximumPartySize = Math.min(settings.maximumPartySize, selectedArea?.capacity ?? 0)

  const numericPartySize = Number(partySize)

  const validSelection = Boolean(
    selectedAreaId &&
    selectedCustomerId &&
    date >= minimumDate &&
    date <= maximumDate &&
    Number.isInteger(numericPartySize) &&
    numericPartySize >= settings.minimumPartySize &&
    numericPartySize <= maximumPartySize,
  )

  useEffect(() => {
    if (!validSelection) return
    let active = true
    const timer = window.setTimeout(() => {
      setSlots([])
      setIsAvailabilityLoading(true)
      void onLoadAvailability(selectedAreaId, date, numericPartySize, reservation?.id)
        .then((data) => {
          if (!active || !data) return
          const currentTimeExists = data.some((slot) => slot.time.slice(0, 5) === time)
          setSlots(!currentTimeExists && time ? [{ time, isAvailable: true }, ...data] : data)
        })
        .finally(() => {
          if (active) setIsAvailabilityLoading(false)
        })
    }, 250)
    return () => {
      active = false
      window.clearTimeout(timer)
    }
  }, [selectedAreaId, date, numericPartySize, reservation?.id, validSelection])

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={reservation ? t("management.edit_reservation") : t("management.new_reservation")}
              icon={faCalendarCheck}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <PopupHelpButton
                    helpKey="restaurant-reservation-save"
                    variant="global"
                    title={t("help.save.title")}
                    description={t("help.save.description")}
                    bullets={t("help.save.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:restaurantReservations.savePopup.close"
                  />
                </div>
              }
            />

            <form
              className="space-y-3 p-3"
              onSubmit={async (event) => {
                event.preventDefault()
                if (!validSelection || !time) return
                if (
                  await onSave({
                    id: reservation?.id ?? 0,
                    customerId: selectedCustomerId,
                    areaId: selectedAreaId,
                    date,
                    time,
                    partySize: numericPartySize,
                    notes: notes.trim() || undefined,
                  })
                )
                  onClose()
              }}
            >
              {activeAreas.length === 0 && (
                <p className="rounded-lg bg-amber-50 p-3 text-sm font-bold text-amber-800">
                  {t("management.no_active_areas")}
                </p>
              )}

              <TitlePageResponsiveIconSmall
                icon={faUser}
                iconColor={selectedCustomer ? THEME.colors.iconBGSecondaryColor : THEME.colors.iconBGColor}
                boxShadow="none"
                title={selectedCustomer?.personEntity.name}
                subtitle={selectedCustomer ? undefined : t("management.select_customer")}
                subtitleChildren={
                  selectedCustomer ? (
                    <BasicCard title={selectedCustomer.personEntity.cpf} borderColor={THEME.colors.white} />
                  ) : undefined
                }
                children={
                  <ButtonIcon
                    typeButton="button"
                    sizeNumber={SIZE.buttonExpandIcon}
                    bgColor={THEME.colors.buttonBGPlusColor}
                    iconColor={THEME.colors.buttonFGPlusColor}
                    icon={faMagnifyingGlass}
                    onClick={() => setIsCustomerSelectionOpen(true)}
                    size="small"
                    descriptionKey="interactionDescriptions:restaurantReservations.savePopup.customer"
                  />
                }
              />

              <GenericDropDown
                legend={t("fields.area")}
                initialState={String(selectedAreaId)}
                data={activeAreas.map((area) => ({ label: area.name, value: String(area.id) }))}
                handlerFuncion={(event) => {
                  setAreaId(Number(event.target.value))
                  setTime("")
                }}
                descriptionKey="interactionDescriptions:restaurantReservations.savePopup.area"
              />

              <div className="grid gap-3 sm:grid-cols-2">
                <InputField
                  type="date"
                  icon={faCalendarCheck}
                  value={date}
                  min={minimumDate}
                  max={maximumDate}
                  required
                  placeholder={t("fields.date")}
                  onChange={(event) => {
                    if (event.target.value) {
                      setDate(event.target.value)
                      setTime("")
                    }
                  }}
                  descriptionKey="interactionDescriptions:restaurantReservations.savePopup.date"
                />

                <InputField
                  type="number"
                  icon={faPeopleGroup}
                  value={partySize}
                  min={settings.minimumPartySize}
                  max={maximumPartySize}
                  required
                  placeholder={t("fields.party_size")}
                  onChange={(event) => {
                    setPartySize(event.target.value)
                    setTime("")
                  }}
                  descriptionKey="interactionDescriptions:restaurantReservations.savePopup.partySize"
                />
              </div>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="rounded-lg border=2 border-slate-200 p-3">
                  <p className="mb-2 text-sm font-black text-slate-700">{t("public.times_title")}</p>

                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {validSelection &&
                      slots.map((slot) => (
                        <button
                          type="button"
                          key={slot.time}
                          disabled={!slot.isAvailable}
                          onClick={() => setTime(slot.time.slice(0, 5))}
                          data-interaction-description-key="interactionDescriptions:restaurantReservations.savePopup.time"
                          className={`rounded-lg border px-2 py-2 text-sm font-black transition ${
                            time === slot.time.slice(0, 5)
                              ? "border-orange-500 bg-orange-500 text-white"
                              : "border-slate-200 bg-white text-slate-700"
                          } disabled:opacity-30`}
                        >
                          {slot.time.slice(0, 5)}
                        </button>
                      ))}
                  </div>

                  {isAvailabilityLoading && (
                    <p className="py-3 text-center text-sm font-semibold text-slate-500">{t("common.loading")}</p>
                  )}

                  {validSelection && !isAvailabilityLoading && !slots.some((slot) => slot.isAvailable) && (
                    <p className="py-3 text-center text-sm font-semibold text-slate-500">{t("public.no_times")}</p>
                  )}
                </div>
              </Animate>

              <InputField
                type="text"
                icon={faUser}
                value={notes}
                maxLength={300}
                placeholder={t("fields.notes")}
                onChange={(event) => setNotes(event.target.value)}
                descriptionKey="interactionDescriptions:restaurantReservations.savePopup.notes"
              />

              <div className={defaultStyle.buttonsController}>
                <Button
                  label={isLoading ? t("common.saving") : t("actions.save_reservation")}
                  typeButton="submit"
                  disabled={isLoading || !validSelection || !time}
                  bgColor={THEME.colors.buttonBGSaveColor}
                  descriptionKey="interactionDescriptions:restaurantReservations.savePopup.save"
                />
              </div>
            </form>
          </section>
        </Animate>

        {isCustomerSelectionOpen && (
          <CostumerList
            listCards={customerMemory.getListCards(true)}
            onClinck={(id) => {
              const customer = customerMemory.findById(id)
              if (!customer || customer.isDefaultCustomer) return
              setCustomerId(customer.id)
              setIsCustomerSelectionOpen(false)
            }}
            onClonse={() => setIsCustomerSelectionOpen(false)}
          />
        )}
      </div>
    </ModalPortal>
  )
}
